import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, Sparkle, PaperPlaneTilt, MagnifyingGlass, FileText, Check,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const CONTENT_TYPES = ["Social Post", "Email", "Blog Article", "Ad Copy"];
const STATUSES = [
  { value: "draft", label: "Draft", cls: "border-white/15 text-ink-secondary" },
  { value: "approved", label: "Approved", cls: "border-emerald-400/30 bg-emerald-400/5 text-emerald-200" },
  { value: "scheduled", label: "Scheduled", cls: "border-sky-400/30 bg-sky-400/5 text-sky-200" },
  { value: "published", label: "Published", cls: "border-fuchsia-400/30 bg-fuchsia-400/5 text-fuchsia-200" },
];

const emptyForm = {
  title: "", content_type: "", pillar_id: "", channel_id: "",
  body: "", status: "draft", scheduled_date: "", notes: "",
};

function statusPill(s) {
  const st = STATUSES.find(x => x.value === s) || STATUSES[0];
  return <span data-testid={`content-status-pill-${s}`} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>;
}

export default function CampaignContent({ campaignId }) {
  const [items, setItems] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [pillarFilter, setPillarFilter] = useState("__all__");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, pl, ch] = await Promise.all([
        api.get(`/campaigns/${campaignId}/content`),
        api.get(`/campaigns/${campaignId}/pillars`).catch(() => ({ data: [] })),
        api.get(`/channels`).catch(() => ({ data: [] })),
      ]);
      setItems(it.data); setPillars(pl.data || []); setChannels(ch.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load content");
      setItems([]);
    } finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "__all__") list = list.filter(x => x.status === statusFilter);
    if (pillarFilter !== "__all__") list = list.filter(x => (x.pillar_id || "") === (pillarFilter === "__none__" ? "" : pillarFilter));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(x => (x.title || "").toLowerCase().includes(q));
    }
    return list;
  }, [items, query, statusFilter, pillarFilter]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it.content_id);
    setForm({
      title: it.title || "", content_type: it.content_type || "",
      pillar_id: it.pillar_id || "", channel_id: it.channel_id || "",
      body: it.body || "", status: it.status || "draft",
      scheduled_date: it.scheduled_date || "", notes: it.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const title = form.title.trim();
    if (!title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title, content_type: form.content_type || null,
        pillar_id: form.pillar_id || (editing ? "" : null),
        channel_id: form.channel_id || (editing ? "" : null),
        body: form.body, status: form.status,
        scheduled_date: form.scheduled_date || null,
        notes: form.notes,
      };
      // On create, remove empty-string clears
      if (!editing) {
        if (!payload.pillar_id) delete payload.pillar_id;
        if (!payload.channel_id) delete payload.channel_id;
      }
      if (editing) {
        const { data } = await api.patch(`/content/${editing}`, payload);
        setItems(all => all.map(x => x.content_id === editing ? data : x));
        toast.success("Content updated");
      } else {
        const { data } = await api.post(`/campaigns/${campaignId}/content`, payload);
        setItems(all => [data, ...all]);
        toast.success("Content added");
      }
      setDialogOpen(false); setForm(emptyForm); setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doGenerate = async () => {
    if (!editing) {
      toast.error("Save this item first, then generate a draft");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`/content/${editing}/generate`, {});
      setForm(prev => ({ ...prev, body: data.draft_body }));
      toast.success("Draft ready — review, edit, then click Save.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/content/${confirmDelete.content_id}`);
      setItems(all => all.filter(x => x.content_id !== confirmDelete.content_id));
      toast.success(`Removed "${confirmDelete.title}"`);
    } catch { toast.error("Could not remove"); }
    finally { setConfirmDelete(null); }
  };

  return (
    <div className="space-y-4" data-testid="content-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary/80 flex items-center gap-1.5">
            <FileText weight="duotone" className="h-3 w-3" />
            Content items
          </h4>
          <p className="mt-1 text-xs text-ink-secondary max-w-xl">
            The actual copy — posts, emails, articles, ads — you plan to run for this campaign.
            Optionally tie each item to a Pillar and a Channel; generate a first draft with AI and edit before saving.
          </p>
        </div>
        <button data-testid="content-add" onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100">
          <Plus weight="bold" className="h-4 w-4" /> New content
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
          <input data-testid="content-search" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search by title"
            className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30" />
        </div>
        <select data-testid="content-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="h-9 rounded-md bg-graphite-800 border border-white/10 px-3 text-xs text-ink-primary">
          <option value="__all__">All statuses</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select data-testid="content-pillar-filter" value={pillarFilter} onChange={e => setPillarFilter(e.target.value)}
          className="h-9 rounded-md bg-graphite-800 border border-white/10 px-3 text-xs text-ink-primary">
          <option value="__all__">All pillars</option>
          <option value="__none__">No pillar</option>
          {pillars.map(p => <option key={p.pillar_id} value={p.pillar_id}>{p.name}</option>)}
        </select>
      </div>

      <div className="rounded-md border border-white/10 bg-graphite-950/40 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-ink-secondary">Loading content…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="content-empty">
            <FileText weight="duotone" className="mx-auto h-8 w-8 text-ink-secondary/50" />
            <div className="mt-3 text-sm text-ink-secondary">
              {items.length === 0 ? "No content yet. Add the first item above." : "No matches for this filter."}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="content-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Pillar</th>
                  <th className="px-4 py-3 font-medium">Channel</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map(it => (
                  <tr key={it.content_id} data-testid={`content-row-${it.content_id}`} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-ink-primary" data-testid={`content-title-${it.content_id}`}>{it.title}</td>
                    <td className="px-4 py-3 text-ink-secondary text-xs">{it.content_type || "—"}</td>
                    <td className="px-4 py-3 text-ink-secondary text-xs">{it.pillar_name || "—"}</td>
                    <td className="px-4 py-3 text-ink-secondary text-xs">{it.channel_name || "—"}</td>
                    <td className="px-4 py-3">{statusPill(it.status)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button data-testid={`content-edit-${it.content_id}`} onClick={() => openEdit(it)}
                          className="p-1.5 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"><PencilSimple weight="bold" className="h-3.5 w-3.5" /></button>
                        <button data-testid={`content-delete-${it.content_id}`} onClick={() => setConfirmDelete(it)}
                          className="p-1.5 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"><Trash weight="bold" className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm(emptyForm); setEditing(null); } } }}>
        <DialogContent data-testid="content-dialog" className="max-w-2xl bg-graphite-900 border-white/10 text-ink-primary max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">{editing ? "Edit content item" : "New content item"}</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Draft the copy for one post, email, article or ad. AI can suggest a starting draft — you review before saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Title</label>
              <Input data-testid="content-form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} maxLength={200} className="bg-graphite-950 border-white/10" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Type</label>
                <select data-testid="content-form-type" value={form.content_type}
                  onChange={e => setForm({ ...form, content_type: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  <option value="">— None —</option>
                  {CONTENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Status</label>
                <select data-testid="content-form-status" value={form.status}
                  onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Pillar</label>
                <select data-testid="content-form-pillar" value={form.pillar_id}
                  onChange={e => setForm({ ...form, pillar_id: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  <option value="">— None —</option>
                  {pillars.map(p => <option key={p.pillar_id} value={p.pillar_id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Channel</label>
                <select data-testid="content-form-channel" value={form.channel_id}
                  onChange={e => setForm({ ...form, channel_id: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  <option value="">— None —</option>
                  {channels.map(c => <option key={c.channel_id} value={c.channel_id}>{c.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Scheduled date</label>
                <Input data-testid="content-form-scheduled" type="date" value={form.scheduled_date}
                  onChange={e => setForm({ ...form, scheduled_date: e.target.value })}
                  className="bg-graphite-950 border-white/10" />
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Body</label>
                <button data-testid="content-form-generate" onClick={doGenerate}
                  disabled={generating || !editing}
                  title={!editing ? "Save this item first, then generate a draft" : "Generate AI draft from campaign strategy + pillar"}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">
                  <Sparkle weight="bold" className="h-3 w-3" />
                  {generating ? "Generating…" : "Generate draft"}
                </button>
              </div>
              <Textarea data-testid="content-form-body" value={form.body}
                onChange={e => setForm({ ...form, body: e.target.value })} rows={10}
                placeholder="Write the copy here, or click Generate draft to seed with AI (only after first save)."
                className="bg-graphite-950 border-white/10 text-sm" />
              {!editing && (
                <p className="text-[11px] text-ink-secondary/60">Generate draft becomes available after this item is saved once.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Notes</label>
              <Textarea data-testid="content-form-notes" value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                className="bg-graphite-950 border-white/10 text-sm" />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <button data-testid="content-form-cancel" onClick={() => { if (!saving) { setDialogOpen(false); setForm(emptyForm); setEditing(null); } }} disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">Cancel</button>
            <button data-testid="content-form-save" onClick={save} disabled={saving || !form.title.trim()}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Create content"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmDelete && (
        <Dialog open onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
          <DialogContent data-testid="content-delete-dialog" className="max-w-md bg-graphite-900 border-white/10 text-ink-primary">
            <DialogHeader>
              <DialogTitle>Remove content item?</DialogTitle>
              <DialogDescription className="text-ink-secondary">
                "{confirmDelete.title}" will be soft-deleted. The campaign brief, pillars and strategy are untouched.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button onClick={() => setConfirmDelete(null)}
                className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5">Cancel</button>
              <button data-testid="content-delete-confirm" onClick={doDelete}
                className="rounded-full bg-red-400/90 text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-red-300">Remove</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
